// src/context/AuthContext.jsx
// ✅ COMPLETE FIXED VERSION
// Key fixes vs previous version:
// 1. googleLogin popup: correctly waits for postMessage from AuthCallback
// 2. Fallback poll: checks both token AND user in localStorage
// 3. refreshUser: fast path sets state immediately, doesn't clear on API error
// 4. _silentRefresh: skips gracefully if no refreshToken (OAuth users)

import React, {
  createContext, useState, useContext,
  useEffect, useCallback, useRef,
} from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const initDone = useRef(false);

  const dispatchAuthEvent = () =>
    window.dispatchEvent(new CustomEvent('authStateChanged'));

  const persistSession = (userData, accessToken, refreshTokenVal) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user',  JSON.stringify(userData));
    if (refreshTokenVal)
      localStorage.setItem('refreshToken', refreshTokenVal);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    setUser(null);
    dispatchAuthEvent();
  };

  // ── INIT ─────────────────────────────────────────────────
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    const initAuth = async () => {
      try {
        const storedUser  = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');

        if (!storedToken || !storedUser) { setLoading(false); return; }

        let parsedUser = null;
        try { parsedUser = JSON.parse(storedUser); } catch { /* corrupted */ }
        if (parsedUser) setUser(parsedUser);

        try {
          const res       = await authAPI.verify();
          const freshUser = res?.data?.user || parsedUser;
          if (freshUser) {
            localStorage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
          }
        } catch (err) {
          const status = err?.response?.status;
          if (status === 401) {
            const ok = await _silentRefresh();
            if (!ok) clearSession();
          } else if (!status) {
            console.warn('Network error during verify — keeping session');
          } else {
            clearSession();
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        const stored = localStorage.getItem('user');
        setUser(stored ? JSON.parse(stored) : null);
      }
    };
    const handleAuthEvent = () => {
      const stored = localStorage.getItem('user');
      setUser(stored ? JSON.parse(stored) : null);
    };

    window.addEventListener('storage',          handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthEvent);
    return () => {
      window.removeEventListener('storage',          handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthEvent);
    };
  }, []);

  // ── SILENT REFRESH ────────────────────────────────────────
  const _silentRefresh = async () => {
    try {
      const storedRefresh = localStorage.getItem('refreshToken');
      // ✅ OAuth (Google) users don't have refreshToken — skip gracefully
      if (!storedRefresh || storedRefresh === '') return false;

      const res          = await authAPI.refreshToken({ refresh_token: storedRefresh });
      const access_token = res?.data?.access_token;
      if (!access_token) return false;

      localStorage.setItem('token', access_token);

      try {
        const verify = await authAPI.verify();
        const fresh  = verify?.data?.user;
        if (fresh) { localStorage.setItem('user', JSON.stringify(fresh)); setUser(fresh); }
      } catch { /* token valid, user fetch failed */ }

      return true;
    } catch { return false; }
  };

  // ── REFRESH USER ─────────────────────────────────────────
  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) { setUser(null); return; }

    // ✅ Fast path — update state from localStorage immediately
    const stored = localStorage.getItem('user');
    if (stored) {
      try { setUser(JSON.parse(stored)); dispatchAuthEvent(); } catch { /* ignore */ }
    }

    // Slow path — fetch fresh profile
    try {
      const res   = await authAPI.me();
      const fresh = res.data?.user || res.data;
      if (fresh && fresh.id) {
        localStorage.setItem('user', JSON.stringify(fresh));
        setUser(fresh);
        dispatchAuthEvent();
      }
    } catch {
      try {
        const res   = await authAPI.verify();
        const fresh = res.data?.user;
        if (fresh) { localStorage.setItem('user', JSON.stringify(fresh)); setUser(fresh); dispatchAuthEvent(); }
      } catch {
        // Both failed — keep localStorage user, don't clear
        console.warn('refreshUser: API unreachable, keeping cached user');
      }
    }
  }, []);

  // ── LOGIN ─────────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authAPI.login({ email, password });
      const { user: userData, access_token, refresh_token } = response.data;
      persistSession(userData, access_token, refresh_token);
      setUser(userData);
      dispatchAuthEvent();
      toast.success('Welcome back! Login successful.');
      return { success: true, user: userData };
    } catch (error) {
      const msg = error.response?.data?.message || 'Login failed. Please check your credentials.';
      toast.error(msg);
      return { success: false, error: msg };
    } finally { setLoading(false); }
  };

  // ── REGISTER ──────────────────────────────────────────────
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      const { user: newUser, access_token, refresh_token } = response.data;
      persistSession(newUser, access_token, refresh_token);
      setUser(newUser);
      dispatchAuthEvent();
      toast.success('Registration successful! Welcome to Fruit Export Platform.');
      return { success: true, user: newUser };
    } catch (error) {
      const msg = error.response?.data?.message || 'Registration failed';
      toast.error(msg);
      return { success: false, error: msg };
    } finally { setLoading(false); }
  };

  // ── GOOGLE LOGIN ─────────────────────────────────────────
  // Flow:
  //   1. Open Google auth URL in popup
  //   2. Google → GoogleCallback.jsx (popup) → backend process-code
  //   3. Backend returns redirect_url = /auth/callback?key=xxx
  //   4. Popup navigates to AuthCallback.jsx
  //   5. AuthCallback exchanges key → token, saves to localStorage
  //   6. AuthCallback sends postMessage({ type:'GOOGLE_AUTH_REDIRECT' }) to parent
  //   7. googleLogin hears postMessage → calls refreshUser() → navigates parent
  const googleLogin = async () => {
    try {
      const response = await authAPI.googleLogin();
      if (!response.data?.success || !response.data?.auth_url) {
        throw new Error('Failed to get Google login URL');
      }

      const width  = 600, height = 650;
      const left   = Math.round(window.screen.width  / 2 - width  / 2);
      const top    = Math.round(window.screen.height / 2 - height / 2);

      const popup = window.open(
        response.data.auth_url,
        'Google Login',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
      );

      // Popup blocked → navigate same tab
      if (!popup || popup.closed) {
        window.location.href = response.data.auth_url;
        return { success: true };
      }

      return new Promise((resolve) => {

        // ── postMessage from AuthCallback ─────────────────────
        const handleMessage = async (event) => {
          if (event.origin !== window.location.origin)    return;
          if (event.data?.type !== 'GOOGLE_AUTH_REDIRECT') return;

          window.removeEventListener('message', handleMessage);
          clearInterval(pollTimer);
          clearTimeout(safetyTimer);

          // ✅ Token already in localStorage (saved by AuthCallback)
          // Just sync React state
          await refreshUser();
          toast.success('Google login successful!');
          resolve({ success: true });

          const dest = event.data.redirectUrl || '/buyer/dashboard';
          setTimeout(() => { window.location.href = dest; }, 150);
        };

        window.addEventListener('message', handleMessage);

        // ── Fallback: popup closed without postMessage ────────
        const pollTimer = setInterval(async () => {
          if (!popup.closed) return;

          clearInterval(pollTimer);
          clearTimeout(safetyTimer);
          window.removeEventListener('message', handleMessage);

          const token      = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');

          if (token && storedUser) {
            // ✅ AuthCallback finished but postMessage was missed
            await refreshUser();
            toast.success('Google login successful!');
            resolve({ success: true });

            const u    = JSON.parse(storedUser);
            const path = !u?.user_type            ? '/auth/select-role'
                       : u.user_type === 'admin'  ? '/admin/dashboard'
                       : u.user_type === 'seller' ? '/seller/dashboard'
                       :                            '/buyer/dashboard';

            setTimeout(() => { window.location.href = path; }, 150);
          } else {
            // Popup closed without completing login
            resolve({ success: false, error: 'Login cancelled' });
          }
        }, 500);

        // ── Safety timeout 5 min ──────────────────────────────
        const safetyTimer = setTimeout(() => {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          try { if (!popup.closed) popup.close(); } catch { /* ignore */ }
          resolve({ success: false, error: 'Login timed out' });
        }, 5 * 60 * 1000);
      });

    } catch (error) {
      const msg = error.response?.data?.message || 'Google login failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  // ── HANDLE GOOGLE CALLBACK (non-popup path) ───────────────
  const handleGoogleCallback = async (code) => {
    try {
      setLoading(true);
      const response = await authAPI.googleCallback({ code });
      const { user: userData, access_token, refresh_token } = response.data;
      persistSession(userData, access_token, refresh_token);
      setUser(userData);
      dispatchAuthEvent();
      toast.success('Google login successful!');
      return { success: true, user: userData };
    } catch (error) {
      const msg = error.response?.data?.message || 'Google authentication failed';
      toast.error(msg);
      return { success: false, error: msg };
    } finally { setLoading(false); }
  };

  // ── LOGOUT ────────────────────────────────────────────────
  const logout = () => {
    clearSession();
    toast.success('Logged out successfully');
    setTimeout(() => { window.location.href = '/'; }, 300);
  };

  // ── UPDATE USER (local) ───────────────────────────────────
  const updateUser = (updatedData) => {
    try {
      const current = JSON.parse(localStorage.getItem('user') || '{}');
      const merged  = { ...current, ...updatedData };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(merged);
      dispatchAuthEvent();
      return { success: true, user: merged };
    } catch {
      return { success: false, error: 'Failed to update user profile' };
    }
  };

  // ── UPDATE USER TYPE ──────────────────────────────────────
  const updateUserType = async (userType) => {
    try {
      const response = await authAPI.updateUserType({ user_type: userType });
      const updated  = response.data.user;
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      dispatchAuthEvent();
      toast.success(`Profile updated to ${userType === 'buyer' ? 'Buyer' : 'Seller'}`);
      return { success: true, user: updated };
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update user type';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  // ── REFRESH TOKEN (axios interceptor) ────────────────────
  const refreshToken = async () => {
    const ok = await _silentRefresh();
    if (!ok) { logout(); return null; }
    return localStorage.getItem('token');
  };

  // ── CONTEXT ───────────────────────────────────────────────
  const value = {
    user, loading,
    login, googleLogin, handleGoogleCallback,
    register, logout,
    updateUser, updateUserType,
    refreshToken, refreshUser,
    isAuthenticated:    !!user,
    isAdmin:            user?.user_type === 'admin',
    isBuyer:            user?.user_type === 'buyer',
    isSeller:           user?.user_type === 'seller',
    needsRoleSelection: user && !user.user_type,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};